// Sidebar hierarchy from the approved admin sequence workbook. Internal page
// keys are used so translated labels cannot accidentally change the ordering.
export const ADMIN_MENU_STRUCTURE = Object.freeze([
    {
        key: 'dashboard-summary',
        name: 'Dashboard / Summary',
        subsections: [
            {
                key: 'executive-view',
                name: 'Executive View',
                moduleKeys: ['rental', 'invoices', 'equipments'],
            },
        ],
    },
    {
        key: 'rental-management',
        name: 'Rental Management',
        subsections: [
            {
                key: 'order-intake',
                name: 'Order Intake',
                moduleKeys: ['ticketing', 'ordersRental'],
            },
            {
                key: 'exceptions-service',
                name: 'Exceptions & Service',
                moduleKeys: ['breakdowns'],
            },
            {
                key: 'archive-removed',
                name: 'Archive / Removed',
                moduleKeys: ['recyclebin'],
            },
        ],
    },
    {
        key: 'billing-payments',
        name: 'Billing & Payments',
        subsections: [
            {
                key: 'invoicing',
                name: 'Invoicing',
                moduleKeys: ['paymentInvoices'],
            },
            {
                key: 'payments',
                name: 'Payments',
                moduleKeys: ['paymentHistory'],
            },
            {
                key: 'vouchers',
                name: 'Vouchers',
                moduleKeys: ['vouchers', 'subscriptionVouchers'],
            },
        ],
    },
    {
        key: 'equipment-catalogue',
        name: 'Equipment Catalogue',
        subsections: [
            {
                key: 'knowledge-base',
                name: 'Knowledge Base',
                moduleKeys: ['wiki'],
            },
            {
                key: 'classification',
                name: 'Classification',
                moduleKeys: ['categories', 'attributes'],
            },
            {
                key: 'branding',
                name: 'Branding',
                moduleKeys: ['brands', 'featuredBrand'],
            },
            {
                key: 'content',
                name: 'Content',
                moduleKeys: ['introcutions'],
            },
        ],
    },
    {
        key: 'web-store',
        name: 'Web Store',
        subsections: [
            {
                key: 'plans-subscribers',
                name: 'Plans & Subscribers',
                moduleKeys: ['subscriptionPlan', 'usersSubscriptions'],
            },
            {
                key: 'requests-activity',
                name: 'Requests & Activity',
                moduleKeys: ['subscription', 'subscriptionLogs'],
            },
        ],
    },
    {
        key: 'content-management',
        name: 'Content Management',
        subsections: [
            {
                key: 'core-pages',
                name: 'Core Pages',
                moduleKeys: ['staticPages'],
            },
            {
                key: 'media-promotions',
                name: 'Media & Promotions',
                moduleKeys: ['wallpapers', 'announcements'],
            },
            {
                key: 'help-content',
                name: 'Help Content',
                moduleKeys: ['faq', 'userFAQs'],
            },
            {
                key: 'editorial',
                name: 'Editorial',
                moduleKeys: ['blogs', 'userBlogs', 'blogCategories'],
            },
            {
                key: 'resources',
                name: 'Resources',
                moduleKeys: ['resources', 'resourceCategories'],
            },
            {
                key: 'legal',
                name: 'Legal',
                moduleKeys: ['userAgreements'],
            },
        ],
    },
    {
        key: 'customer-support',
        name: 'Customer Support',
        subsections: [
            {
                key: 'enquiries',
                name: 'Enquiries',
                moduleKeys: ['enquiries', 'demoRequests'],
            },
        ],
    },
    {
        key: 'location-compliance',
        name: 'Location & Compliance',
        subsections: [
            {
                key: 'geography',
                name: 'Geography',
                moduleKeys: ['countries', 'cities', 'addresses'],
            },
            {
                key: 'compliance',
                name: 'Compliance',
                moduleKeys: ['isurances'],
            },
        ],
    },
    {
        key: 'users-access',
        name: 'Users & Access',
        subsections: [
            {
                key: 'user-management',
                name: 'User Management',
                moduleKeys: ['users'],
            },
            {
                key: 'staff-management',
                name: 'Staff Management',
                moduleKeys: ['employees'],
            },
            {
                key: 'roles-permissions',
                name: 'Roles & Permissions',
                moduleKeys: ['permissions'],
            },
        ],
    },
    {
        key: 'system-admin',
        name: 'System & Admin',
        subsections: [
            {
                key: 'communications',
                name: 'Communications',
                moduleKeys: ['emails'],
            },
            {
                key: 'audit-monitoring',
                name: 'Audit & Monitoring',
                moduleKeys: ['loggers', 'logs'],
            },
            {
                key: 'automation',
                name: 'Automation',
                moduleKeys: ['AutoGens'],
            },
        ],
    },
]);

export const ADMIN_MENU_SEQUENCE = Object.freeze(
    ADMIN_MENU_STRUCTURE.reduce(
        (keys, section) => section.subsections.reduce(
            (sectionKeys, subsection) => sectionKeys.concat(
                subsection.moduleKeys,
            ),
            keys,
        ),
        [],
    ),
);

// This page was added after the workbook was produced. Keeping its placement
// explicit prevents a future workbook refresh from hiding a live feature.
const ADMIN_MENU_EXTENSIONS = Object.freeze([
    { key: 'additionalEquipmentPlan', sectionKey: 'web-store', subsectionKey: 'plans-subscribers', afterKey: 'additinalCreditsPlan', beforeKey: 'usersSubscriptions' },
    {
        key: 'additinalCreditsPlan',
        sectionKey: 'web-store',
        subsectionKey: 'plans-subscribers',
        afterKey: 'subscriptionPlan',
        beforeKey: 'usersSubscriptions',
    },
]);

const insertExtension = (modules, extension, module) => {
    const afterIndex = modules.findIndex(
        ({ key }) => key === extension.afterKey,
    );

    if (afterIndex !== -1) {
        modules.splice(afterIndex + 1, 0, module);
        return;
    }

    const beforeIndex = modules.findIndex(
        ({ key }) => key === extension.beforeKey,
    );

    modules.splice(beforeIndex === -1 ? modules.length : beforeIndex, 0, module);
};

export const buildAdminMenuSections = (modules = []) => {
    if (!Array.isArray(modules)) {
        return [];
    }

    const moduleByKey = new Map();
    modules.forEach((module) => {
        if (module?.key && !moduleByKey.has(module.key)) {
            moduleByKey.set(module.key, module);
        }
    });

    const placedKeys = new Set();
    const sections = ADMIN_MENU_STRUCTURE.map((section) => ({
        key: section.key,
        name: section.name,
        subsections: section.subsections.map((subsection) => ({
            key: subsection.key,
            name: subsection.name,
            modules: subsection.moduleKeys
                .map((key) => {
                    const module = moduleByKey.get(key);
                    if (module) {
                        placedKeys.add(key);
                    }
                    return module;
                })
                .filter(Boolean),
        })),
    }));

    ADMIN_MENU_EXTENSIONS.forEach((extension) => {
        const module = moduleByKey.get(extension.key);
        if (!module || placedKeys.has(extension.key)) {
            return;
        }

        const section = sections.find(
            ({ key }) => key === extension.sectionKey,
        );
        const subsection = section?.subsections.find(
            ({ key }) => key === extension.subsectionKey,
        );

        if (subsection) {
            insertExtension(subsection.modules, extension, module);
            placedKeys.add(extension.key);
        }
    });

    const populatedSections = sections
        .map((section) => ({
            ...section,
            subsections: section.subsections.filter(
                ({ modules: subsectionModules }) => (
                    subsectionModules.length > 0
                ),
            ),
        }))
        .filter(({ subsections }) => subsections.length > 0);

    // Future configured pages stay reachable until the workbook is updated.
    // Parentless internal helpers keep their existing hidden behavior.
    const unlistedModules = modules.filter((module) => (
        module?.key
        && module.parent
        && !placedKeys.has(module.key)
    ));

    if (unlistedModules.length > 0) {
        populatedSections.push({
            key: 'other',
            name: 'Other',
            subsections: [{
                key: 'uncategorised',
                name: 'Uncategorised',
                modules: unlistedModules,
            }],
        });
    }

    return populatedSections;
};
